// meetingRooms/components/prices/styled.js - Honey-themed styling for MeetingRooms pricing components only
import { styled } from '@mui/material/styles';
import { Box, Typography, Button, Card, Alert } from '@mui/material';

// Honey-inspired color palette (matching WorkAreas)
const honeyColors = {
  goldenHoney: '#F1C40F',
  brightHoney: '#F4D03F',
  honeycomb: '#D4A574',
  darkHoney: '#B8860B',
  honeyBrown: '#5D4E37',
  softCream: '#FFFEF7',
  warmAmber: '#CD853F',
  lightBrown: '#8B7355',
  honeyGradient: 'linear-gradient(135deg, #F1C40F, #F4D03F)',
  honeycombGradient: 'linear-gradient(135deg, #D4A574, #F1C40F)',
};

// Main container for pricing section only
export const HoneyPricingContainer = styled(Box)(({ theme }) => ({
  fontFamily: "'Inter', -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif",
  color: honeyColors.honeyBrown,
}));

// Header section
export const HoneyPricingHeader = styled(Box)(({ theme }) => ({
  textAlign: 'center',
  marginBottom: theme.spacing(3),
}));

// Title with honey gradient  
export const HoneyPricingTitle = styled(Box)(({ theme }) => ({
  fontSize: '2.5rem',
  fontWeight: 700,
  marginBottom: theme.spacing(0.5),
  background: honeyColors.honeycombGradient,
  backgroundClip: 'text',
  WebkitBackgroundClip: 'text',
  WebkitTextFillColor: 'transparent',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  gap: theme.spacing(1),
}));

// Subtitle
export const HoneyPricingSubtitle = styled(Box)(({ theme }) => ({
  color: honeyColors.lightBrown,
  fontSize: '1.1rem',
  fontWeight: 400,
}));

// VAT Toggle Styling
export const StyledVATToggle = styled(Box)(({ theme }) => ({
  backgroundColor: honeyColors.softCream,
  borderRadius: '16px',
  padding: theme.spacing(2),
  border: `2px solid ${honeyColors.brightHoney}30`,
  marginBottom: theme.spacing(3),
  boxShadow: '0 4px 12px rgba(212, 165, 116, 0.15)',
}));

// Tariff Card Styling (similar to WorkAreas StyledTariffCard)
export const StyledTariffCard = styled(Card)(({ theme }) => ({
  backgroundColor: honeyColors.softCream,
  borderRadius: '24px',
  border: `2px solid ${honeyColors.brightHoney}30`,
  marginBottom: theme.spacing(3),
  boxShadow: '0 8px 32px rgba(93, 78, 55, 0.12)',
  overflow: 'hidden',
  transition: 'all 0.3s ease',
  
  '&:hover': {
    borderColor: `${honeyColors.goldenHoney}50`,
    boxShadow: '0 12px 48px rgba(93, 78, 55, 0.18)',
    transform: 'translateY(-2px)',
  },
}));

// New Tariff Form Card (similar to WorkAreas StyledNewRuleBox)
export const StyledNewTariffCard = styled(Card)(({ theme }) => ({
  backgroundColor: `${honeyColors.goldenHoney}10`,
  borderRadius: '24px',
  border: `2px dashed ${honeyColors.goldenHoney}`,
  marginBottom: theme.spacing(3),
  position: 'relative',
  
  '&::before': {
    content: '"NEW"',
    position: 'absolute',
    top: theme.spacing(1),
    right: theme.spacing(1),
    backgroundColor: honeyColors.goldenHoney,
    color: honeyColors.honeyBrown,
    padding: '4px 8px',
    borderRadius: '8px',
    fontSize: '0.7rem',
    fontWeight: 700,
    zIndex: 1,
  },
}));

// Configuration Section Styling (similar to WorkAreas StyledBasePriceSection)
export const StyledConfigSection = styled(Box)(({ theme }) => ({
  backgroundColor: 'white',
  borderRadius: '16px',
  padding: theme.spacing(2.5),
  border: `1px solid ${honeyColors.brightHoney}30`,
  marginBottom: theme.spacing(2),
}));

// Form Row for inline label + input
export const StyledFormRow = styled(Box)(({ theme }) => ({
  display: 'flex',
  alignItems: 'center',
  marginBottom: theme.spacing(2),
  gap: theme.spacing(2),
}));

// Form Label
export const StyledFormLabel = styled(Typography)(({ theme }) => ({
  width: '120px',
  flexShrink: 0,
  fontSize: '0.875rem',
  fontWeight: 500,
  color: honeyColors.honeyBrown,
}));

// Empty State
export const StyledEmptyState = styled(Alert)(({ theme }) => ({
  borderRadius: '16px',
  backgroundColor: `${honeyColors.brightHoney}10`,
  border: `1px solid ${honeyColors.honeycomb}`,
  color: honeyColors.honeyBrown,
}));

// Loading Container
export const StyledLoadingContainer = styled(Box)(({ theme }) => ({
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
  justifyContent: 'center',
  padding: theme.spacing(4),
  color: honeyColors.lightBrown,
}));

// Add Tariff Button (similar to WorkAreas StyledAddRuleButton)
export const StyledAddTariffButton = styled(Button)(({ theme }) => ({
  borderStyle: 'dashed',
  borderWidth: 2,
  borderColor: honeyColors.honeycomb,
  color: honeyColors.honeycomb,
  borderRadius: '50px',
  padding: theme.spacing(1, 3),
  fontWeight: 600,
  transition: 'all 0.3s ease',
  
  '&:hover': {
    backgroundColor: `${honeyColors.honeycomb}10`,
    borderColor: honeyColors.goldenHoney,
    color: honeyColors.goldenHoney,
  },
}));

// Save Button (similar to WorkAreas StyledSaveButton)
export const StyledSaveButton = styled(Button)(({ theme }) => ({
  background: honeyColors.honeyGradient,
  color: honeyColors.honeyBrown,
  borderRadius: '50px',
  padding: theme.spacing(1.5, 4),
  fontWeight: 700,
  fontSize: '1.1rem',
  boxShadow: '0 8px 32px rgba(241, 196, 15, 0.5)',
  transition: 'all 0.3s ease',
  
  '&:hover': {
    background: honeyColors.honeycombGradient,
    transform: 'translateY(-4px)',
    boxShadow: '0 12px 48px rgba(241, 196, 15, 0.7)',
  },
  
  '&:disabled': {
    opacity: 0.6,
    transform: 'none',
  },
}));

// Status Toggle Button
export const StyledStatusButton = styled(Button)(({ theme }) => ({
  borderRadius: '50px',
  padding: theme.spacing(1, 3),
  fontWeight: 600,
  transition: 'all 0.3s ease',
  
  '&.success-variant': {
    borderColor: '#4caf50',
    color: '#4caf50',
    
    '&:hover': {
      backgroundColor: '#4caf50',
      color: 'white',
    },
  },
  
  '&.error-variant': {
    borderColor: '#f44336',
    color: '#f44336',
    
    '&:hover': {
      backgroundColor: '#f44336',
      color: 'white',
    },
  },
}));